import { emptyDetailsFor } from '../typeCatalog';
import { Asset, AssetOwner, AssetSettings, AssetType, ALL_ASSET_TYPES, normalizeAssetType } from '../types/Asset';
import { normalizeCatalogAttachments } from '../../catalog/catalogAttachments';
import { apiRequest } from '../../../shared/services/apiClient';

export interface AssetListQuery {
  q?: string;
  assetType?: AssetType;
  contactPersonId?: string;
  sortBy?: 'name' | 'assetType' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface CreateAssetRequest {
  name?: string;
  assetType?: AssetType;
  contactPersonIds?: string[];
  owners?: AssetOwner[];
  identifier?: string;
  details?: Record<string, unknown>;
}

export interface UpdateAssetRequest {
  name?: string;
  assetType?: AssetType;
  contactPersonIds?: string[];
  owners?: AssetOwner[];
  identifier?: string;
  details?: Record<string, unknown>;
}

export interface UpdateAssetSettingsRequest {
  enabledTypes: AssetType[];
}

interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
}

const normalizeOwner = (value: any): AssetOwner | null => {
  const contactPersonId = String(value?.contactPersonId ?? '');
  if (!contactPersonId) {
    return null;
  }

  return {
    contactPersonId,
    isRegistered: value?.isRegistered !== false,
    isPrimary: Boolean(value?.isPrimary),
  };
};

const normalizeOwners = (value: any, contactPersonIds: string[]): AssetOwner[] => {
  const rawOwners = Array.isArray(value?.owners) ? value.owners : [];
  const fromOwners: AssetOwner[] = rawOwners
    .map((item: unknown) => normalizeOwner(item))
    .filter((owner: AssetOwner | null): owner is AssetOwner => owner !== null);

  if (fromOwners.length > 0) {
    const hasPrimary = fromOwners.some((owner: AssetOwner) => owner.isPrimary);
    return hasPrimary
      ? fromOwners
      : fromOwners.map((owner: AssetOwner, index: number) => ({ ...owner, isPrimary: index === 0 }));
  }

  return contactPersonIds.map((contactPersonId, index) => ({
    contactPersonId,
    isRegistered: true,
    isPrimary: index === 0,
  }));
};

const normalizeDetails = (assetType: AssetType, value: any): Record<string, unknown> => {
  const defaults = emptyDetailsFor(assetType);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return defaults;
  }

  return { ...defaults, ...value };
};

const normalizeAsset = (value: any): Asset => {
  const contactPersonIds = Array.isArray(value?.contactPersonIds)
    ? value.contactPersonIds.map((id: unknown) => String(id)).filter(Boolean)
    : [];
  const owners = normalizeOwners(value, contactPersonIds);
  const assetType = normalizeAssetType(value?.assetType);

  return {
    id: String(value?.id ?? ''),
    name: String(value?.name ?? ''),
    assetType,
    contactPersonIds: owners.length > 0 ? owners.map(owner => owner.contactPersonId) : contactPersonIds,
    owners,
    identifier: value?.identifier ? String(value.identifier) : undefined,
    attachments: normalizeCatalogAttachments(value?.attachments),
    details: normalizeDetails(assetType, value?.details),
    createdAt: value?.createdAt ? String(value.createdAt) : undefined,
    updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
  };
};

const normalizeSettings = (value: any): AssetSettings => {
  const enabledSource = Array.isArray(value?.enabledTypes) ? value.enabledTypes : [];
  const enabledTypes = enabledSource
    .map((item: unknown) => normalizeAssetType(item))
    .filter((type: AssetType, index: number, list: AssetType[]) => list.indexOf(type) === index);

  return {
    id: String(value?.id ?? ''),
    availableTypes: [...ALL_ASSET_TYPES],
    enabledTypes,
    createdAt: value?.createdAt ? String(value.createdAt) : undefined,
    updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
  };
};

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const buildAssetListPath = (query?: AssetListQuery): string => {
  if (!query) {
    return '/api/v1/assets';
  }

  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.assetType) params.set('assetType', query.assetType);
  if (query.contactPersonId) params.set('contactPersonId', query.contactPersonId);
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDir) params.set('sortDir', query.sortDir);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));

  const queryString = params.toString();
  return queryString ? `/api/v1/assets?${queryString}` : '/api/v1/assets';
};

export const assetService = {
  getAssets: async (query?: AssetListQuery): Promise<Asset[]> => {
    const payload = await apiRequest<PagedResult<any> | any[]>(buildAssetListPath(query));
    return pickItems(payload).map(normalizeAsset);
  },

  createAsset: async (request: CreateAssetRequest): Promise<Asset | null> => {
    const payload = await apiRequest<any>('/api/v1/assets', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const item = pickSingle(payload);
    return item ? normalizeAsset(item) : null;
  },

  getAssetById: async (id: string): Promise<Asset | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/assets/${id}`);
      const item = pickSingle(payload);
      return item ? normalizeAsset(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

    updateAsset: async (id: string, updates: UpdateAssetRequest): Promise<Asset | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/assets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const item = pickSingle(payload);
      return item ? normalizeAsset(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  getSettings: async (): Promise<AssetSettings> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/asset-settings'));
    return normalizeSettings(item);
  },

  updateSettings: async (request: UpdateAssetSettingsRequest): Promise<AssetSettings> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/asset-settings', {
      method: 'PATCH',
      body: JSON.stringify(request),
    }));
    return normalizeSettings(item);
  },

  uploadAttachment: async (id: string, file: File, setAsPrimary = false): Promise<Asset | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('setAsPrimary', setAsPrimary ? 'true' : 'false');
      const item = pickSingle(await apiRequest<any>(`/api/v1/assets/${id}/attachments`, {
        method: 'POST',
        body: formData,
      }));
      return item ? normalizeAsset(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  removeAttachment: async (id: string, attachmentId: string): Promise<Asset | null> => {
    try {
      const item = pickSingle(await apiRequest<any>(`/api/v1/assets/${id}/attachments/${attachmentId}`, {
        method: 'DELETE',
      }));
      return item ? normalizeAsset(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  setPrimaryImage: async (id: string, attachmentId: string): Promise<Asset | null> => {
    try {
      const item = pickSingle(await apiRequest<any>(`/api/v1/assets/${id}/attachments/${attachmentId}/primary`, {
        method: 'POST',
      }));
      return item ? normalizeAsset(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },
};

export const getAssets = assetService.getAssets;
export const createAsset = assetService.createAsset;
export const getAssetById = assetService.getAssetById;
export const updateAsset = assetService.updateAsset;
export const getAssetSettings = assetService.getSettings;
export const updateAssetSettings = assetService.updateSettings;
export const uploadAssetAttachment = assetService.uploadAttachment;
export const removeAssetAttachment = assetService.removeAttachment;
export const setAssetPrimaryImage = assetService.setPrimaryImage;
